(function () {
  'use strict';

  const MOBILE = window.matchMedia('(max-width: 749px)');
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DEFAULT_DURATION = 30; // seconds for one full pass (matches marquee_duration default)
  const MIN_SLIDE_INTERVAL = 1200; // ms floor so many items don't flash by
  const TRUSTPILOT_BOOTSTRAP_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

  const instances = new Map();
  let trustpilotPromise = null;

  function addMediaListener(mq, fn) {
    if (mq.addEventListener) {
      mq.addEventListener('change', fn);
    } else if (mq.addListener) {
      mq.addListener(fn);
    }
  }

  function removeMediaListener(mq, fn) {
    if (mq.removeEventListener) {
      mq.removeEventListener('change', fn);
    } else if (mq.removeListener) {
      mq.removeListener(fn);
    }
  }

  // --- Mobile carousel (desktop is a pure-CSS marquee; this only runs on mobile) ---
  function Carousel(root) {
    this.root = root;
    this.group = root.querySelector('.cf-announcement__group:not(.cf-announcement__group--clone)');
    this.slides = this.group
      ? Array.prototype.slice.call(this.group.querySelectorAll('.cf-announcement__slide'))
      : [];
    this.prevButton = root.querySelector('[data-cf-announcement-prev]');
    this.nextButton = root.querySelector('[data-cf-announcement-next]');
    this.liveRegion = root.querySelector('[data-cf-announcement-live]');
    this.index = 0;
    this.timer = null;
    this.paused = false;
    this.userStopped = false;

    // Nothing to cycle through.
    if (this.slides.length < 2) {
      return;
    }

    // One full pass takes marquee_duration seconds (same setting as the desktop
    // marquee), so each item shows for duration / item-count.
    let duration = Number.parseInt(root.dataset.cfMarqueeDuration, 10);
    if (!duration || duration < 1) {
      duration = DEFAULT_DURATION;
    }
    this.interval = Math.max(MIN_SLIDE_INTERVAL, Math.round((duration * 1000) / this.slides.length));

    root.dataset.cfEnhanced = 'true';

    this.onPrev = this.handlePrev.bind(this);
    this.onNext = this.handleNext.bind(this);
    this.onPointerEnter = this.pause.bind(this);
    this.onPointerLeave = this.resume.bind(this);
    this.onFocusIn = this.pause.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onMediaChange = this.handleMediaChange.bind(this);

    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
    root.addEventListener('mouseenter', this.onPointerEnter);
    root.addEventListener('mouseleave', this.onPointerLeave);
    root.addEventListener('focusin', this.onFocusIn);
    root.addEventListener('focusout', this.onFocusOut);
    addMediaListener(MOBILE, this.onMediaChange);

    this.show(0);
    this.start();
  }

  Carousel.prototype.show = function (i) {
    const count = this.slides.length;
    this.index = ((i % count) + count) % count;
    this.slides.forEach((slide, n) => {
      slide.classList.toggle('is-active', n === this.index);
    });
  };

  Carousel.prototype.announce = function () {
    if (!this.liveRegion) return;
    const active = this.slides[this.index];
    const label = active ? active.textContent.replace(/\s+/g, ' ').trim() : '';
    this.liveRegion.textContent = this.index + 1 + ' of ' + this.slides.length + ': ' + label;
  };

  Carousel.prototype.start = function () {
    this.stopTimer();
    if (!MOBILE.matches || REDUCE.matches || this.userStopped || this.paused) {
      return;
    }
    this.timer = window.setInterval(() => {
      this.show(this.index + 1);
    }, this.interval);
  };

  Carousel.prototype.stopTimer = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  // Temporary pause (hover / focus) — resumes when the pointer/focus leaves.
  Carousel.prototype.pause = function () {
    this.paused = true;
    this.stopTimer();
  };

  Carousel.prototype.resume = function () {
    this.paused = false;
    this.start();
  };

  Carousel.prototype.handleFocusOut = function (event) {
    if (!this.root.contains(event.relatedTarget)) {
      this.resume();
    }
  };

  // Using an arrow is an explicit take-over: stop auto-rotation for good (WCAG 2.2.2).
  Carousel.prototype.handlePrev = function () {
    this.userStopped = true;
    this.stopTimer();
    this.show(this.index - 1);
    this.announce();
  };

  Carousel.prototype.handleNext = function () {
    this.userStopped = true;
    this.stopTimer();
    this.show(this.index + 1);
    this.announce();
  };

  Carousel.prototype.handleMediaChange = function () {
    this.show(this.index);
    this.start();
  };

  Carousel.prototype.destroy = function () {
    this.stopTimer();
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    this.root.removeEventListener('mouseenter', this.onPointerEnter);
    this.root.removeEventListener('mouseleave', this.onPointerLeave);
    this.root.removeEventListener('focusin', this.onFocusIn);
    this.root.removeEventListener('focusout', this.onFocusOut);
    removeMediaListener(MOBILE, this.onMediaChange);
  };

  // --- Trustpilot (loaded only when a block embeds the widget) ---
  function isTrustpilotReady() {
    return Boolean(window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function');
  }

  function whenTrustpilotReady() {
    if (trustpilotPromise) {
      return trustpilotPromise;
    }

    trustpilotPromise = new Promise(function (resolve, reject) {
      if (isTrustpilotReady()) {
        resolve();
        return;
      }

      let existing = document.querySelector('script[src*="widget.trustpilot.com/bootstrap"]');
      if (!existing) {
        existing = document.createElement('script');
        existing.src = TRUSTPILOT_BOOTSTRAP_SRC;
        existing.async = true;
        document.head.appendChild(existing);
      }

      let attempts = 0;
      const timer = window.setInterval(function () {
        attempts += 1;
        if (isTrustpilotReady()) {
          window.clearInterval(timer);
          resolve();
        } else if (attempts >= 60) {
          window.clearInterval(timer);
          reject(new Error('Trustpilot API unavailable'));
        }
      }, 50);
    });

    return trustpilotPromise;
  }

  function hydrateTrustpilot(root) {
    root
      .querySelectorAll('.cf-announcement__group:not(.cf-announcement__group--clone) .trustpilot-widget')
      .forEach(function (widget) {
        if (!widget.querySelector('iframe')) {
          window.Trustpilot.loadFromElement(widget, true);
        }
      });
  }

  function initTrustpilot(root) {
    whenTrustpilotReady()
      .then(function () {
        hydrateTrustpilot(root);
      })
      .catch(function () {
        /* Bootstrap unavailable; the widget container stays empty. */
      });
  }

  // --- Wiring ---
  function initBar(root) {
    const id = root.dataset.sectionId;
    if (!id || instances.has(id)) {
      return;
    }

    instances.set(id, { carousel: new Carousel(root) });

    if (root.dataset.cfHasTrustpilot === 'true') {
      initTrustpilot(root);
    }
  }

  function destroyBar(id) {
    const entry = instances.get(String(id));
    if (!entry) {
      return;
    }
    if (entry.carousel?.destroy) {
      entry.carousel.destroy();
    }
    instances.delete(String(id));
  }

  function initAll() {
    document.querySelectorAll('[data-cf-announcement-bar]').forEach(initBar);
  }

  initAll();

  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target;
    if (!section || typeof section.querySelector !== 'function') {
      return;
    }
    const root = section.querySelector('[data-cf-announcement-bar]');
    if (root) {
      destroyBar(root.dataset.sectionId);
      initBar(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.detail && event.detail.sectionId) {
      destroyBar(event.detail.sectionId);
    }
  });
})();
